// File input controller (mirrors @a-safe-digital/react-file-input — CONEK-1612).
//
// Owns the selected file list via a DataTransfer kept in sync with the hidden
// <input type="file"> (so the form submits the real files). Handles drag & drop
// with visual feedback, dedupes by name on multiple uploads, enforces maxFiles
// (and the CONEK maxSize byte limit), and renders a preview grid by cloning the
// FilePreviewComponent <template>.
//
// Targets:
//   input       - hidden <input type="file"> (the submit source)
//   dropzone    - visible drop area (border toggles on drag)
//   rowTemplate  - <template> holding one FilePreviewComponent (all variants)
//   preview     - grid container the preview cards are appended to
//
// Values:
//   multiple    - allow more than one file
//   maxFiles    - cap on file count (0 = unlimited)
//   maxSize     - per-file byte limit (0 = unlimited) [CONEK extension]
//   deleteLabel - aria-label for the delete button

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropzone", "rowTemplate", "preview", "error"]
  static values = {
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 0 },
    maxSize: { type: Number, default: 0 },
    deleteLabel: { type: String, default: "Delete" },
    invalidTypeMessage: {
      type: String,
      default: "This file type is not allowed",
    },
    maxSizeMessage: { type: String, default: "" },
  }

  connect() {
    this.files = []
    this._urls = []
  }

  disconnect() {
    this._urls.forEach((url) => url && URL.revokeObjectURL(url))
  }

  // --- Drag & drop ---

  dragEnter(event) {
    event.preventDefault()
    if (!this.inputTarget.disabled) this._addDragStyles()
  }

  dragOver(event) {
    event.preventDefault()
    if (!this.inputTarget.disabled) this._addDragStyles()
  }

  dragLeave(event) {
    event.preventDefault()
    // Ignore drags moving onto a child element of the dropzone.
    if (!this.dropzoneTarget.contains(event.relatedTarget))
      this._removeDragStyles()
  }

  drop(event) {
    event.preventDefault()
    this._removeDragStyles()
    if (this.inputTarget.disabled) return

    const dropped = Array.from(event.dataTransfer.files)
    this._addFiles(dropped, this.multipleValue ? this.maxFilesValue : 1)
  }

  // --- Native selection ---

  fileSelected() {
    const selected = Array.from(this.inputTarget.files)
    this._addFiles(selected, this.multipleValue ? this.maxFilesValue : 1)
  }

  // --- Remove (wired on each cloned delete button) ---

  removeFile(event) {
    if (!this.hasPreviewTarget) return
    const card = event.currentTarget.closest('[data-fp="card"]')
    if (!card) return
    const index = Array.from(this.previewTarget.children).indexOf(card)
    if (index < 0) return

    this.files.splice(index, 1)
    // Removing a file is a fresh user action; clear any stale inline error left
    // over from a prior add (e.g. a rejected file that was dropped alongside a
    // valid one which the user has now deleted).
    this._clearError()
    this._sync()
    this._renderPreviews()
  }

  // --- Internals ---

  _addFiles(incoming, limit) {
    if (incoming.length === 0) return

    // Validate type (accept) first, then size — matching the order users expect.
    // The first rejection's message is surfaced inline (and via file-input:error).
    let firstError = null
    const accepted = incoming.filter((file) => {
      if (!this._matchesAccept(file)) {
        if (!firstError) firstError = this.invalidTypeMessageValue
        return false
      }
      if (!this._withinSize(file)) {
        if (!firstError) firstError = this._tooLargeMessage(file)
        return false
      }
      return true
    })

    if (firstError) {
      this._showError(firstError)
    } else {
      this._clearError()
    }

    if (this.multipleValue) {
      const incomingNames = new Set(accepted.map((f) => f.name))
      const kept = this.files.filter((f) => !incomingNames.has(f.name))
      this.files = this._limit([...kept, ...accepted], limit)
    } else if (accepted.length > 0) {
      this.files = accepted.slice(-1)
    }

    this._sync()
    this._renderPreviews()
  }

  // Keep only the last `max` files (matches React's limitFiles). 0 = unlimited.
  _limit(files, max) {
    return max > 0 ? files.slice(-max) : files
  }

  _withinSize(file) {
    return this.maxSizeValue <= 0 || file.size <= this.maxSizeValue
  }

  // Mirror the native `accept` attribute for drag & drop (which bypasses it).
  // Tokens are extensions (".pdf"), exact MIME ("image/png"), or wildcard
  // ("image/*"). Empty accept = everything allowed.
  _matchesAccept(file) {
    const tokens = (this.inputTarget.accept || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)
    if (tokens.length === 0) return true

    const name = file.name.toLowerCase()
    const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : ""
    const type = (file.type || "").toLowerCase()

    return tokens.some((token) => {
      if (token.startsWith(".")) return ext === token
      if (token.endsWith("/*")) return type.startsWith(token.slice(0, -1))
      return type === token
    })
  }

  _tooLargeMessage(file) {
    if (this.maxSizeMessageValue) return this.maxSizeMessageValue
    const maxMB = (this.maxSizeValue / (1024 * 1024)).toFixed(1)
    return `${file.name} exceeds the maximum size of ${maxMB}MB`
  }

  _showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
    // Keep emitting the event so consumers can react (logging, custom UI).
    this.element.dispatchEvent(
      new CustomEvent("file-input:error", {
        bubbles: true,
        detail: { message },
      }),
    )
  }

  _clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  // Push the internal list into the hidden input so the form submits it.
  _sync() {
    const dataTransfer = new DataTransfer()
    this.files.forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
  }

  _renderPreviews() {
    if (!this.hasPreviewTarget || !this.hasRowTemplateTarget) return

    this._urls.forEach((url) => url && URL.revokeObjectURL(url))
    this._urls = []
    this.previewTarget.replaceChildren()

    this.files.forEach((file) => {
      const card =
        this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
      const variant = this._variant(file.type)

      card.querySelectorAll("[data-fp-variant]").forEach((node) => {
        if (node.dataset.fpVariant === variant) {
          node.removeAttribute("hidden")
        } else {
          node.remove()
        }
      })

      if (variant === "image") {
        const url = URL.createObjectURL(file)
        this._urls.push(url)
        const thumb = card.querySelector('[data-fp="thumb"]')
        if (thumb) {
          thumb.src = url
          thumb.alt = file.name
        }
      } else {
        this._urls.push(null)
      }

      const nameEl = card.querySelector('[data-fp="name"]')
      if (nameEl) nameEl.textContent = file.name

      const dateEl = card.querySelector('[data-fp="date"]')
      if (dateEl) {
        dateEl.textContent = new Date(file.lastModified).toLocaleString()
        dateEl.removeAttribute("hidden")
      }

      const deleteEl = card.querySelector('[data-fp="delete"]')
      if (deleteEl) deleteEl.setAttribute("aria-label", this.deleteLabelValue)

      this.previewTarget.appendChild(card)
    })
  }

  // Keep in step with Ui::FileTypeVariant#file_variant_for — the Ruby side
  // renders the variant nodes this reveals by name, so a type the two disagree
  // about reveals nothing and the card draws with no icon at all.
  _variant(type) {
    const t = (type || "").toLowerCase()
    if (t.startsWith("image")) return "image"
    if (t.startsWith("video")) return "video"
    if (t === "application/pdf") return "pdf"
    if (t === "application/msword" || t.includes("word")) return "word"
    return "generic"
  }

  _addDragStyles() {
    this.dropzoneTarget.classList.add("border-primary")
    this.dropzoneTarget.classList.remove("border-input")
  }

  _removeDragStyles() {
    this.dropzoneTarget.classList.remove("border-primary")
    this.dropzoneTarget.classList.add("border-input")
  }
}
